import * as THREE from "three";
import { PhysicsFriction, PhysicsMass, PhysicsRestitution } from "../physics/physics_masses.js";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { ResourceManager } from "../resource_loader/resource_manager.js";
import { ResourceID } from "../resource_loader/resource_id.js";
import { Vfx3DManager } from "../vfx_3d/vfx_3d_manager.js";
import { PhysicsTable } from "./physics_table.js";
import { audioController } from "../audio/audio_controller.js";
import { AudioClips } from "../audio/audio_constants.js";
import { SimulationContext } from "../simulation/simulation_context.js";
import { EntityKind, LevelEntity } from "../types/level_entity.js";
import { hapticsController, VibrationContext } from "../haptics/haptics_controller.js";
import { HapticPattern } from "../haptics/haptics_constants.js";
import { BreakablePhysicsEntity } from "./breakable_physics_entity.js";
import { edgeSupportFor } from "../physics/edge_support_pad.js";
import { GlobalSettings } from "../global_settings.js";
import { BrickMasonry } from "./brick_masonry.js";
import { BlockImpactMaterial } from "../vfx_3d/effects/ball_impact/vfx_ball_block_impact_task.js";

// One entry per cube material/skin. Stone, brick and both columns have their own
// models (see createStoneMesh / createBrickMesh / createColumnMesh); plywood
// and plastic share the authored box family.
export enum CubeMaterialType
{
  PLYWOOD,
  STONE,
  BRICK,
  PLASTIC,
  COLUMN,
  ROUND_COLUMN
}

export class PhysicsCube extends BreakablePhysicsEntity implements LevelEntity
{
  private outside_timer = 0;

  private outside_pending = false;
  private previous_bottom_y: number | null = null;
  private previous_table_bottom_y: number | null = null;
  private drop_sound_played = false;

  private readonly mass: number;
  private readonly material_type: CubeMaterialType;
  private readonly texture_id?: ResourceID;
  private readonly width: number;
  private readonly height: number;

  private readonly far_destroy_delay = SimulationContext.enabled ? 0.4 : 2;

  private static size: THREE.Vector3;

  public readonly kind = EntityKind.Cube;

  // Increase the shared grid cell and every block derived from it so level
  // constructions occupy the play area more prominently on phone screens.
  public static readonly DEFAULT_SCALE = 0.32;

  // Matches the gap_x/gap_z ratio GameScreen#createLayeredBlocks uses between grid cells —
  // merged multi-cell pieces must stretch by this much extra so two merged pieces sitting
  // in adjacent grid cells end up exactly one gap apart, same as two 1x1 pieces would.
  public static readonly GRID_GAP_RATIO = 0.02;

  // Use symmetric vertical nine-slice cuts. This preserves the centred frame
  // and star, while keeping the upper and lower stretched areas equal.
  private static readonly VERTICAL_CAP_CUT_RATIO = 0.60;
  // Keep the horizontal stretch bands outside the centre emblem. A smaller
  // cut caught the outer edges of the green crown and widened it on long
  // blocks.
  private static readonly HORIZONTAL_CAP_CUT_RATIO = 0.58;

  private static readonly long_geometries = new Map<string, THREE.BufferGeometry>();
  private static readonly AUTHORED_MODELS: Record<number, ResourceID> = {
    1: ResourceID.Block1x1Model,
    2: ResourceID.Block1x2Model,
    3: ResourceID.Block1x3Model,
    4: ResourceID.Block1x4Model,
    5: ResourceID.Block1x5Model,
  };
  private static readonly PLASTIC_MODELS: Record<number, ResourceID> = {
    1: ResourceID.PlasticBlock1x1Model,
    2: ResourceID.PlasticBlock1x2Model,
    3: ResourceID.PlasticBlock1x3Model,
    4: ResourceID.PlasticBlock1x4Model,
    5: ResourceID.PlasticBlock1x5Model,
  };
  private static readonly STONE_MODELS: Record<number, ResourceID> = {
    1: ResourceID.StoneBlock1x1Model,
    2: ResourceID.StoneBlock1x2Model,
    3: ResourceID.StoneBlock1x3Model,
    4: ResourceID.StoneBlock1x4Model,
  };
  // Where the stone atlas can be cut when a model has to grow past its authored
  // length, as fractions of the half-length from the centre: `cut` keeps the
  // lily and the stone around it whole, `band` is the plain stone between the
  // lily and the chipped rim that gets stretched. Measured on stone_M_baseColor.
  private static readonly STONE_1X4_LENGTH_SLICE = { cut: 0.2, band: 0.65 };
  private static readonly STONE_2X2_WIDTH_SLICE = { cut: 0.32, band: 0.55 };
  private static readonly STONE_2X2_HEIGHT_SLICE = { cut: 0.34, band: 0.52 };
  // stone_M_baseColor bakes its seams and the ring around the lily almost
  // black, and light only scales the texel, so the darks are lifted in the
  // shader: texels below the median stone tone (linear luma) are pulled
  // toward it along a power curve; the median and everything above stay put.
  private static readonly STONE_SHADOW_MID = 0.148;
  private static readonly STONE_SHADOW_GAMMA = 0.7;
  private static readonly COLUMN_MODELS: Record<number, ResourceID> = {
    1: ResourceID.Column1x1Model,
    2: ResourceID.Column1x2Model,
    3: ResourceID.Column1x3Model,
    4: ResourceID.Column1x4Model,
  };
  private static readonly ROUND_COLUMN_MODELS: Record<number, ResourceID> = {
    1: ResourceID.RoundColumn1x1Model,
    2: ResourceID.RoundColumn1x2Model,
    3: ResourceID.RoundColumn1x3Model,
    4: ResourceID.RoundColumn1x4Model,
  };
  private static readonly AUTHORED_BOX_TEXTURES = new Set<ResourceID>( [
    ResourceID.PurpleBoxTexture,
    ResourceID.GreenBoxTexture,
    ResourceID.PurpleBox2x2Texture,
    ResourceID.GreenBox2x2Texture,
  ] );
  private static readonly PLASTIC_TEXTURES = new Set<ResourceID>( [
    ResourceID.PlasticRedTexture,
    ResourceID.PlasticRed2x2Texture,
    ResourceID.PlasticBlueTexture,
    ResourceID.PlasticBlue2x2Texture,
    ResourceID.PlasticYellowTexture,
    ResourceID.PlasticYellow2x2Texture,
    ResourceID.PlasticOrangeTexture,
    ResourceID.PlasticOrange2x2Texture,
    ResourceID.PlasticGreenTexture,
    ResourceID.PlasticGreen2x2Texture,
    ResourceID.PlasticPinkTexture,
    ResourceID.PlasticPink2x2Texture,
    ResourceID.PlasticLightBlueTexture,
    ResourceID.PlasticLightBlue2x2Texture,
    ResourceID.PlasticPurpleTexture,
    ResourceID.PlasticPurple2x2Texture,
  ] );
  private static readonly PLASTIC_2X2_TEXTURES = new Map<ResourceID, ResourceID>( [
    [ ResourceID.PlasticRedTexture, ResourceID.PlasticRed2x2Texture ],
    [ ResourceID.PlasticBlueTexture, ResourceID.PlasticBlue2x2Texture ],
    [ ResourceID.PlasticYellowTexture, ResourceID.PlasticYellow2x2Texture ],
    [ ResourceID.PlasticOrangeTexture, ResourceID.PlasticOrange2x2Texture ],
    [ ResourceID.PlasticGreenTexture, ResourceID.PlasticGreen2x2Texture ],
    [ ResourceID.PlasticPinkTexture, ResourceID.PlasticPink2x2Texture ],
    [ ResourceID.PlasticLightBlueTexture, ResourceID.PlasticLightBlue2x2Texture ],
    [ ResourceID.PlasticPurpleTexture, ResourceID.PlasticPurple2x2Texture ],
  ] );
  private static readonly VARIANTS = [
    { model: ResourceID.StoneBlock1x1Model, texture: ResourceID.StoneTexture, normal: ResourceID.StoneNormal, mass_key: 'stone_box' as const, friction: PhysicsFriction.stone, material: CubeMaterialType.STONE },
    { model: ResourceID.Block1x1Model, texture: ResourceID.BrickDebrisTexture, normal: undefined, mass_key: 'brick_box' as const, friction: PhysicsFriction.brick, material: CubeMaterialType.BRICK },
    { model: ResourceID.Block1x1Model, texture: ResourceID.GreenBoxTexture, normal: ResourceID.AuthoredBoxNormal, mass_key: 'colored_plywood_box' as const, friction: PhysicsFriction.plywood, material: CubeMaterialType.PLYWOOD },
    { model: ResourceID.PlasticBlock1x1Model, texture: ResourceID.PlasticRedTexture, normal: ResourceID.PlasticNewNormal, mass_key: 'plastic_box' as const, friction: PhysicsFriction.plastic, material: CubeMaterialType.PLASTIC },
    { model: ResourceID.PlasticBlock1x1Model, texture: ResourceID.PlasticBlueTexture, normal: ResourceID.PlasticNewNormal, mass_key: 'plastic_box' as const, friction: PhysicsFriction.plastic, material: CubeMaterialType.PLASTIC },
    { model: ResourceID.PlasticBlock1x1Model, texture: ResourceID.PlasticYellowTexture, normal: ResourceID.PlasticNewNormal, mass_key: 'plastic_box' as const, friction: PhysicsFriction.plastic, material: CubeMaterialType.PLASTIC },
    { model: ResourceID.PlasticBlock1x1Model, texture: ResourceID.PlasticOrangeTexture, normal: ResourceID.PlasticNewNormal, mass_key: 'plastic_box' as const, friction: PhysicsFriction.plastic, material: CubeMaterialType.PLASTIC },
    { model: ResourceID.PlasticBlock1x1Model, texture: ResourceID.PlasticGreenTexture, normal: ResourceID.PlasticNewNormal, mass_key: 'plastic_box' as const, friction: PhysicsFriction.plastic, material: CubeMaterialType.PLASTIC },
    { model: ResourceID.PlasticBlock1x1Model, texture: ResourceID.PlasticPinkTexture, normal: ResourceID.PlasticNewNormal, mass_key: 'plastic_box' as const, friction: PhysicsFriction.plastic, material: CubeMaterialType.PLASTIC },
    { model: ResourceID.Block1x1Model, texture: ResourceID.PurpleBoxTexture, normal: ResourceID.AuthoredBoxNormal, mass_key: 'plywood_box' as const, friction: PhysicsFriction.plywood, material: CubeMaterialType.PLYWOOD },
    { model: ResourceID.PlasticBlock1x1Model, texture: ResourceID.PlasticLightBlueTexture, normal: ResourceID.PlasticNewNormal, mass_key: 'plastic_box' as const, friction: PhysicsFriction.plastic, material: CubeMaterialType.PLASTIC },
    { model: ResourceID.Block1x1Model, texture: ResourceID.PurpleBoxTexture, normal: ResourceID.AuthoredBoxNormal, mass_key: 'plywood_box' as const, friction: PhysicsFriction.plywood, material: CubeMaterialType.PLYWOOD },
    { model: ResourceID.Block1x1Model, texture: ResourceID.PurpleBoxTexture, normal: ResourceID.AuthoredBoxNormal, mass_key: 'plywood_box' as const, friction: PhysicsFriction.plywood, material: CubeMaterialType.PLYWOOD },
    { model: ResourceID.PlasticBlock1x1Model, texture: ResourceID.PlasticPurpleTexture, normal: ResourceID.PlasticNewNormal, mass_key: 'plastic_box' as const, friction: PhysicsFriction.plastic, material: CubeMaterialType.PLASTIC },
    { model: ResourceID.Column1x1Model, texture: ResourceID.ColumnTexture, normal: ResourceID.ColumnNormal, mass_key: 'column_per_cell' as const, friction: PhysicsFriction.stone, material: CubeMaterialType.COLUMN },
    { model: ResourceID.RoundColumn1x1Model, texture: ResourceID.RoundColumnTexture, normal: ResourceID.RoundColumnNormal, mass_key: 'column_per_cell' as const, friction: PhysicsFriction.stone, material: CubeMaterialType.ROUND_COLUMN },
  ];

  constructor(
    position: THREE.Vector3,
    width = 1,
    height = 1,
    material?: CubeMaterialType | CubeMaterialType[],
    variant_index?: number,
    rotation?: { x?: number; y?: number; z?: number },
    surface_tilt_rad = 0
  )
  {
    const variant = PhysicsCube.selectVariant( material, variant_index );
    const mesh = PhysicsCube.createMesh( width, height, variant, rotation );
    const mass = PhysicsMass[variant.mass_key] * width * height;


    super( mesh, position, {
      angular_damping: 0,
      angular_rest_threshold_sq: 0.01,
      arm_delay: 0,
      friction: variant.friction,
      // X-tilt shifts the block's centre of mass forward/back off whatever's
      // under it (unlike Z, which balances symmetrically on a tip, or Y,
      // which doesn't shift the centre of mass at all) — that's a genuinely
      // marginal equilibrium, not just "not enough grip", so angular damping
      // alone can slow a tip-over but can't stop one that's actually
      // underway. Some linear damping keeps a settling nudge from turning
      // into a slide that starts that tip in the first place.
      linear_damping: 0,
      linear_rest_threshold_sq: 0.01,
      mass,
      restitution: PhysicsRestitution.solid_block,
      initial_rotation_x: rotation?.x,
      initial_rotation_y: rotation?.y,
      initial_rotation_z: rotation?.z,
      extra_colliders: edgeSupportFor( mesh, rotation, surface_tilt_rad ),
    } );

    this.width = width;
    this.height = height;
    this.texture_id = variant.texture;
    this.mass = mass;
    this.material_type = variant.material;
  }

  private static selectVariant(
    material?: CubeMaterialType | CubeMaterialType[],
    variant_index?: number
  ): ( typeof PhysicsCube.VARIANTS )[number]
  {
    const allowed = material === undefined ? undefined : Array.isArray( material ) ? material : [ material ];
    const pool = allowed === undefined ? PhysicsCube.VARIANTS : PhysicsCube.VARIANTS.filter( v => allowed.includes( v.material ) );
    const indexed = variant_index === undefined ? undefined : PhysicsCube.VARIANTS[variant_index];

    if ( indexed !== undefined && ( allowed === undefined || allowed.includes( indexed.material ) ) )
      return indexed;

    if ( pool.length === 0 )
    {
      const requested = allowed?.map( entry => CubeMaterialType[entry] ).join( ", " ) ?? "ANY";
      throw new Error( `[PhysicsCube] No variants registered for material: ${requested}` );
    }

    if ( variant_index !== undefined )
      console.warn( `[PhysicsCube] Variant index ${variant_index} is unavailable for the requested material; using its first registered skin` );

    return pool[Math.floor( Math.random() * pool.length )];
  }

  private static createMesh(
    width: number,
    height: number,
    variant: ( typeof PhysicsCube.VARIANTS )[number],
    rotation?: { x?: number; y?: number; z?: number }
  ): THREE.Object3D
  {
    if ( variant.material === CubeMaterialType.STONE )
      return PhysicsCube.createStoneMesh( width, height, variant.texture, variant.normal );

    if ( variant.material === CubeMaterialType.BRICK )
      return PhysicsCube.createBrickMesh( width, height );

    if ( variant.material === CubeMaterialType.COLUMN )
      return PhysicsCube.createColumnMesh( PhysicsCube.COLUMN_MODELS, width, height, variant.texture, variant.normal );

    if ( variant.material === CubeMaterialType.ROUND_COLUMN )
      return PhysicsCube.createColumnMesh( PhysicsCube.ROUND_COLUMN_MODELS, width, height, variant.texture, variant.normal );

    const authored_model = PhysicsCube.getAuthoredModel( width, height, variant.material );
    if ( authored_model !== undefined )
      return PhysicsCube.createAuthoredMesh( width, height, variant.texture, variant.normal, authored_model, rotation );

    const mesh = ResourceManager.getModel( variant.model );

    mesh.traverse( child =>
    {
      if ( !( child instanceof THREE.Mesh ) )
        return;

      child.castShadow = true;
      // Merged horizontal nine-slice bands are coplanar and can self-shadow,
      // making the stretched face look much darker than a regular cube.
      child.receiveShadow = width === 1 && height === 1;
      child.material = Array.isArray( child.material )
        ? child.material.map( m => PhysicsCube.createSmoothMaterial( m, variant.texture ) )
        : PhysicsCube.createSmoothMaterial( child.material, variant.texture );

      if ( width > 1 || height > 1 )
      {
        child.geometry = PhysicsCube.prepareGridGeometry(
          variant.model,
          child.geometry as THREE.BufferGeometry,
          width,
          height
        );
      }
    } );

    mesh.scale.setScalar( PhysicsCube.DEFAULT_SCALE );

    return mesh;
  }

  private static createAuthoredMesh(
    width: number,
    height: number,
    texture_id: ResourceID | undefined,
    normal_id: ResourceID | undefined,
    model_id: ResourceID,
    rotation?: { x?: number; y?: number; z?: number }
  ): THREE.Object3D
  {
    const blocks = Math.max( width, height );
    const is_plastic = texture_id !== undefined && PhysicsCube.PLASTIC_TEXTURES.has( texture_id );
    const is_square_2x2 = width === 2 && height === 2;
    const plastic_2x2_texture_id = is_square_2x2 && texture_id !== undefined
      ? PhysicsCube.PLASTIC_2X2_TEXTURES.get( texture_id )
      : undefined;
    const uses_plastic_2x2_maps = plastic_2x2_texture_id !== undefined;
    const uses_purple_box_2x2_maps = is_square_2x2
      && texture_id === ResourceID.PurpleBoxTexture;
    const uses_green_box_2x2_maps = is_square_2x2
      && texture_id === ResourceID.GreenBoxTexture;
    const uses_dedicated_2x2_atlas = uses_plastic_2x2_maps
      || uses_purple_box_2x2_maps
      || uses_green_box_2x2_maps;
    const material_texture_id = uses_plastic_2x2_maps
      ? plastic_2x2_texture_id
      : uses_purple_box_2x2_maps
        ? ResourceID.PurpleBox2x2Texture
        : uses_green_box_2x2_maps
          ? ResourceID.GreenBox2x2Texture
          : texture_id;
    const material_normal_id = uses_plastic_2x2_maps
      ? ResourceID.Plastic2x2Normal
      : uses_purple_box_2x2_maps || uses_green_box_2x2_maps
        ? ResourceID.Block2x2Normal
        : normal_id;

    // The supplied 1x4/1x5 box files are 1x1 meshes with a different name.
    // Continue the real 1x3 geometry along its authored local length axis,
    // matching the way the tree family grows from 1 to 5 sections. This keeps
    // the 1x3 caps, bevel and centre ornament at their authored proportions.
    const geometry_model_id = blocks >= 4
      ? is_plastic ? ResourceID.PlasticBlock1x3Model : ResourceID.Block1x3Model
      : model_id;
    const flip_texture_y = true;
    const visual = ResourceManager.getModel( geometry_model_id );
    const oriented = new THREE.Group();
    oriented.add( visual );

    if ( !GlobalSettings.IS_PROD && plastic_2x2_texture_id !== undefined )
      PhysicsCube.logPlastic2x2Setup(
        "source",
        visual,
        plastic_2x2_texture_id,
        ResourceID.Plastic2x2Normal
      );

    if (
      PhysicsCube.AUTHORED_BOX_TEXTURES.has( texture_id! )
      && geometry_model_id === ResourceID.Block1x3Model
      && height > width
    )
    {
      // block_1x3 is authored upside down relative to block_1x1/block_1x2.
      // The generated 1x4/1x5 variants inherit that source orientation too.
      visual.rotation.z = Math.PI;
    }
    else if ( width > height )
    {
      // Authored long models are vertical. Rotate the complete model for a
      // horizontal piece, exactly like the other authored block families.
      visual.rotation.z = -Math.PI * 0.5;
    }

    visual.traverse( child =>
    {
      if ( !( child instanceof THREE.Mesh ) )
        return;

      if ( blocks >= 4 )
      {
        child.geometry = PhysicsCube.prepareAuthoredLongGeometry(
          geometry_model_id,
          child.geometry as THREE.BufferGeometry,
          blocks,
          is_plastic
        );
      }
      else if ( uses_dedicated_2x2_atlas )
      {
        child.geometry = PhysicsCube.prepare2x2AtlasGeometry(
          child.geometry as THREE.BufferGeometry
        );
      }

      child.castShadow = true;
      // Single blocks need the soft neighbour/contact shadows visible in the
      // reference. Keep them off on stretched authored meshes to avoid the
      // coplanar self-shadow bands those models can produce.
      child.receiveShadow = false;

      child.material = PhysicsCube.createSmoothMaterial(
        child.material,
        material_texture_id,
        flip_texture_y,
        material_normal_id,
        width,
        height
      );
    } );

    if ( is_plastic && Math.min( width, height ) === 1 )
      PhysicsCube.rollEndCapUpright( oriented, rotation );

    if ( !GlobalSettings.IS_PROD && ( blocks >= 4 || ( width === 2 && height === 2 ) ) )
      PhysicsCube.logAuthoredBlockUVs( visual, width, height, model_id, material_texture_id, material_normal_id );

    oriented.updateMatrixWorld( true );
    const base_box = new THREE.Box3().setFromObject( oriented );
    const base_size = base_box.getSize( new THREE.Vector3() );
    // 2x2 models are authored with their main square panel on the same local
    // front axis as the regular blocks. Keep that panel facing the camera and
    // scale it across two visible grid columns, with regular one-cell depth.
    const target_size = PhysicsCube.getAuthoredTargetSize( width, height );

    oriented.scale.set(
      target_size.x / base_size.x,
      target_size.y / base_size.y,
      target_size.z / base_size.z
    );
    oriented.updateMatrixWorld( true );

    const center = new THREE.Box3().setFromObject( oriented ).getCenter( new THREE.Vector3() );
    oriented.position.sub( center );

    const group = new THREE.Group();
    group.add( oriented );
    if ( !GlobalSettings.IS_PROD && plastic_2x2_texture_id !== undefined )
      PhysicsCube.logPlastic2x2Setup(
        "final",
        group,
        plastic_2x2_texture_id,
        ResourceID.Plastic2x2Normal
      );
    return group;
  }

  private static rollEndCapUpright(
    oriented: THREE.Group,
    rotation: { x?: number; y?: number; z?: number } | undefined
  ): void
  {
    if ( !rotation )
      return;

    const placement = new THREE.Quaternion().setFromEuler(
      new THREE.Euler( rotation.x ?? 0, rotation.y ?? 0, rotation.z ?? 0, "XYZ" )
    );
    oriented.updateMatrixWorld( true );
    const box = new THREE.Box3().setFromObject( oriented );
    const size = box.getSize( new THREE.Vector3() );
    const axes = [ new THREE.Vector3( 1, 0, 0 ), new THREE.Vector3( 0, 1, 0 ), new THREE.Vector3( 0, 0, 1 ) ];
    const extents = [ size.x, size.y, size.z ];
    const longest = Math.max( ...extents );
    const is_cube = Math.min( ...extents ) > longest * 0.99;
    const candidates = is_cube ? axes : axes.filter( ( _axis, i ) => extents[i] === longest );
    const camera_alignment = ( axis: THREE.Vector3 ): number => Math.abs( axis.clone().applyQuaternion( placement ).z );
    const long_axis = candidates.reduce( ( best, axis ) => camera_alignment( axis ) > camera_alignment( best ) ? axis : best );

    if ( camera_alignment( long_axis ) < 0.7 )
      return;

    const center = box.getCenter( new THREE.Vector3() );
    const roll = new THREE.Group();
    const shift = new THREE.Group();
    roll.position.copy( center );
    shift.position.copy( center ).negate();
    for ( const child of [ ...oriented.children ] )
      shift.add( child );
    roll.add( shift );
    oriented.add( roll );

    const probe = new THREE.Group();
    probe.quaternion.copy( placement );
    const parent = oriented.parent;
    probe.add( oriented );

    let best_turn = 0;
    let best_up = -Infinity;
    for ( let turn = 0; turn < 4; turn++ )
    {
      roll.quaternion.setFromAxisAngle( long_axis, turn * Math.PI * 0.5 );
      probe.updateMatrixWorld( true );
      const up = PhysicsCube.facingPictureUp( oriented ).y;
      if ( up > best_up + 1e-3 )
      {
        best_up = up;
        best_turn = turn;
      }
    }

    roll.quaternion.setFromAxisAngle( long_axis, best_turn * Math.PI * 0.5 );
    probe.remove( oriented );
    if ( parent )
      parent.add( oriented );
    oriented.updateMatrixWorld( true );
  }

  private static facingPictureUp( root: THREE.Object3D ): THREE.Vector3
  {
    const up = new THREE.Vector3();
    const a = new THREE.Vector3();
    const b = new THREE.Vector3();
    const c = new THREE.Vector3();
    const ta = new THREE.Vector2();
    const tb = new THREE.Vector2();
    const tc = new THREE.Vector2();

    root.traverse( child =>
    {
      if ( !( child instanceof THREE.Mesh ) )
        return;

      const geometry = child.geometry as THREE.BufferGeometry;
      const position = geometry.getAttribute( "position" ) as THREE.BufferAttribute | undefined;
      const uv = geometry.getAttribute( "uv" ) as THREE.BufferAttribute | undefined;
      if ( !position || !uv )
        return;

      const index = geometry.index;
      const count = index ? index.count : position.count;
      for ( let i = 0; i < count; i += 3 )
      {
        const ia = index ? index.getX( i ) : i;
        const ib = index ? index.getX( i + 1 ) : i + 1;
        const ic = index ? index.getX( i + 2 ) : i + 2;
        a.fromBufferAttribute( position, ia ).applyMatrix4( child.matrixWorld );
        b.fromBufferAttribute( position, ib ).applyMatrix4( child.matrixWorld );
        c.fromBufferAttribute( position, ic ).applyMatrix4( child.matrixWorld );
        const e1 = b.clone().sub( a );
        const e2 = c.clone().sub( a );
        const normal = e1.clone().cross( e2 );
        const area = normal.length();
        if ( area < 1e-8 || normal.z / area < 0.7 )
          continue;

        ta.fromBufferAttribute( uv, ia );
        tb.fromBufferAttribute( uv, ib );
        tc.fromBufferAttribute( uv, ic );
        const du1 = tb.x - ta.x, dv1 = tb.y - ta.y;
        const du2 = tc.x - ta.x, dv2 = tc.y - ta.y;
        const det = du1 * dv2 - du2 * dv1;
        if ( Math.abs( det ) < 1e-10 )
          continue;

        const bitangent = e2.multiplyScalar( du1 ).sub( e1.multiplyScalar( du2 ) ).divideScalar( det );
        up.addScaledVector( bitangent.normalize(), area );
      }
    } );

    return up.normalize();
  }

  /** World size an authored mesh is fitted to: `width` x `height` cells, one cell deep. */
  private static getAuthoredTargetSize( width: number, height: number ): THREE.Vector3
  {
    const target = PhysicsCube.getSize();
    return new THREE.Vector3(
      target.x * width * ( width > 1 ? 1 + PhysicsCube.GRID_GAP_RATIO * ( width - 1 ) / width : 1 ),
      target.y * height,
      target.z
    );
  }

  /**
   * Stone ships a model per footprint (1x1-1x4, 2x2), each with its own island
   * on the stone atlas, so those sizes are used as authored. Any other size
   * starts from the closest model: a shorter piece shrinks the next longer one
   * (at most by a quarter, so the rim and the lily keep their shape), a longer
   * piece stretches the plain stone between the lily and the rim.
   */
  private static createStoneMesh(
    width: number,
    height: number,
    texture_id: ResourceID | undefined,
    normal_id: ResourceID | undefined
  ): THREE.Object3D
  {
    const long = Math.max( width, height );
    const short = Math.min( width, height );
    const is_slab = short > 1;

    const model_length = Math.min( 4, Math.max( 1, Math.ceil( long ) ) );
    const model_id = is_slab ? ResourceID.StoneBlock2x2Model : PhysicsCube.STONE_MODELS[model_length];
    // Growth in source lengths along geometry-local X (width) and Z (the
    // authored vertical axis, see prepareAuthoredLongGeometry). Only a 1x4 past
    // four cells or a slab past 2x2 grows; everything else is fitted by scale.
    const grow_x = is_slab ? width / 2 : 1;
    const grow_z = is_slab ? height / 2 : long / model_length;

    const visual = ResourceManager.getModel( model_id );
    const oriented = new THREE.Group();
    oriented.add( visual );

    // Authored 1xN stones are vertical; lay the whole model down for a
    // horizontal piece, like the other authored block families.
    if ( !is_slab && width > height )
      visual.rotation.z = -Math.PI * 0.5;

    visual.traverse( child =>
    {
      if ( !( child instanceof THREE.Mesh ) )
        return;

      if ( grow_x > 1 || grow_z > 1 )
        child.geometry = PhysicsCube.prepareStoneGeometry(
          model_id,
          child.geometry as THREE.BufferGeometry,
          is_slab,
          grow_x,
          grow_z
        );

      child.castShadow = true;
      child.receiveShadow = false;
      child.material = PhysicsCube.createSmoothMaterial( child.material, texture_id, true, normal_id, width, height );
    } );

    return PhysicsCube.fitToCells( oriented, width, height );
  }

  /** Scales a model to exactly `width` x `height` cells and centres it in a fresh group. */
  private static fitToCells( oriented: THREE.Object3D, width: number, height: number ): THREE.Group
  {
    oriented.updateMatrixWorld( true );
    const base_size = new THREE.Box3().setFromObject( oriented ).getSize( new THREE.Vector3() );
    const target_size = PhysicsCube.getAuthoredTargetSize( width, height );
    oriented.scale.set(
      target_size.x / base_size.x,
      target_size.y / base_size.y,
      target_size.z / base_size.z
    );
    oriented.updateMatrixWorld( true );
    oriented.position.sub( new THREE.Box3().setFromObject( oriented ).getCenter( new THREE.Vector3() ) );

    const group = new THREE.Group();
    group.add( oriented );
    return group;
  }

  private static createBrickMesh( width: number, height: number ): THREE.Object3D
  {
    const visual = new THREE.Mesh( BrickMasonry.getGeometry( width, height ), BrickMasonry.getMaterial() );
    visual.castShadow = true;
    visual.receiveShadow = width === 1 && height === 1;

    const oriented = new THREE.Group();
    oriented.add( visual );
    return PhysicsCube.fitToCells( oriented, width, height );
  }

  /**
   * Columns (square and round) ship as 1x1-1x4 GLBs, each a whole column (plinth,
   * shaft, capital) authored upright. A horizontal piece lays the model on its side; a length
   * past four is fitted by scale, since nothing in the level set asks for it.
   */
  private static createColumnMesh(
    models: Record<number, ResourceID>,
    width: number,
    height: number,
    texture_id: ResourceID | undefined,
    normal_id: ResourceID | undefined
  ): THREE.Object3D
  {
    const long = Math.max( width, height );
    const visual = ResourceManager.getModel( models[Math.min( 4, Math.max( 1, Math.round( long ) ) )] );
    const oriented = new THREE.Group();
    oriented.add( visual );

    if ( width > height )
      visual.rotation.z = -Math.PI * 0.5;

    visual.traverse( child =>
    {
      if ( !( child instanceof THREE.Mesh ) )
        return;

      child.castShadow = true;
      // Self-shadowing darkened a lone 1x1 capital against the longer pieces.
      child.receiveShadow = false;
      // glTF UVs: the column textures load with flipY off.
      child.material = PhysicsCube.createSmoothMaterial( child.material, texture_id, false, normal_id, width, height );
    } );

    return PhysicsCube.fitToCells( oriented, width, height );
  }

  /** Stretches a stone model past its authored length without scaling its rim or lily. */
  private static prepareStoneGeometry(
    model_id: ResourceID,
    source: THREE.BufferGeometry,
    is_slab: boolean,
    grow_x: number,
    grow_z: number
  ): THREE.BufferGeometry
  {
    const key = `stone_${model_id}_${grow_x}x${grow_z}`;
    const cached = PhysicsCube.long_geometries.get( key );
    if ( cached )
      return cached;

    let geometry = source.index ? source.toNonIndexed() : source.clone();
    if ( !geometry.attributes.normal )
      geometry.computeVertexNormals();

    const grow = ( axis: number, factor: number, slice: { cut: number; band: number } ): void =>
    {
      if ( factor <= 1 )
        return;

      geometry.computeBoundingBox();
      const params = PhysicsCube.computeAxisCapParams( geometry, axis );
      params.cap_cut = params.half * slice.cut;
      geometry = PhysicsCube.mergeAlongAxis( geometry, axis, factor, params, 0, slice.band - slice.cut );
    };

    if ( is_slab )
    {
      grow( 0, grow_x, PhysicsCube.STONE_2X2_WIDTH_SLICE );
      grow( 2, grow_z, PhysicsCube.STONE_2X2_HEIGHT_SLICE );
    }
    else
    {
      grow( 2, grow_z, PhysicsCube.STONE_1X4_LENGTH_SLICE );
    }

    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();

    PhysicsCube.long_geometries.set( key, geometry );
    return geometry;
  }

  /** Maps the dedicated 2x2 atlas: square panel on front/back, narrow panel on the edges. */
  private static prepare2x2AtlasGeometry( source: THREE.BufferGeometry ): THREE.BufferGeometry
  {
    const key = `block_2x2_atlas_uv_${source.uuid}`;
    const cached = PhysicsCube.long_geometries.get( key );
    if ( cached )
      return cached;

    // Work triangle-by-triangle. A shared/indexed bevel vertex cannot belong
    // to two atlas islands at once, so split it before assigning face UVs.
    const geometry = source.index ? source.toNonIndexed() : source.clone();
    if ( !geometry.getAttribute( "normal" ) )
      geometry.computeVertexNormals();
    geometry.computeBoundingBox();

    const bounds = geometry.boundingBox!;
    const size = bounds.getSize( new THREE.Vector3() );
    const position = geometry.getAttribute( "position" );
    const normal = geometry.getAttribute( "normal" );
    const uv = geometry.getAttribute( "uv" ).clone();

    const normalized = ( value: number, min: number, length: number ): number =>
      THREE.MathUtils.clamp( ( value - min ) / Math.max( length, 0.0001 ), 0, 1 );

    for ( let triangle = 0; triangle < position.count; triangle += 3 )
    {
      // Select one atlas island for the complete triangle. Choosing it per
      // vertex made rounded bevel triangles interpolate from the front island
      // to the top/bottom island, visibly crushing the lower edge.
      const nx = normal.getX( triangle ) + normal.getX( triangle + 1 ) + normal.getX( triangle + 2 );
      const ny = normal.getY( triangle ) + normal.getY( triangle + 1 ) + normal.getY( triangle + 2 );
      const nz = normal.getZ( triangle ) + normal.getZ( triangle + 1 ) + normal.getZ( triangle + 2 );
      const abs_x = Math.abs( nx );
      const abs_y = Math.abs( ny );
      const abs_z = Math.abs( nz );
      const axis = abs_y >= abs_x && abs_y >= abs_z
        ? "front"
        : abs_x >= abs_z
          ? "side"
          : "cap";

      for ( let vertex = triangle; vertex < triangle + 3; vertex++ )
      {
        const x = normalized( position.getX( vertex ), bounds.min.x, size.x );
        const y = normalized( position.getY( vertex ), bounds.min.y, size.y );
        const z = normalized( position.getZ( vertex ), bounds.min.z, size.z );

        if ( axis === "front" )
        {
          // FBX local +/-Y is the camera-facing 2x2 surface. Project it once
          // across the complete left 2/3 x 2/3 atlas cell.
          uv.setXY( vertex, ( ny < 0 ? x : 1 - x ) * 2 / 3, 1 / 3 + z * 2 / 3 );
        }
        else if ( axis === "side" )
        {
          // Vertical side: depth across U, height across V.
          uv.setXY( vertex, 2 / 3 + ( nx > 0 ? y : 1 - y ) / 3, 1 / 3 + z * 2 / 3 );
        }
        else
        {
          // Top/bottom are 2:1, so rotate the narrow right-hand cell.
          uv.setXY( vertex, 2 / 3 + ( nz > 0 ? y : 1 - y ) / 3, 1 / 3 + ( nz > 0 ? x : 1 - x ) * 2 / 3 );
        }
      }
    }

    uv.needsUpdate = true;
    geometry.setAttribute( "uv", uv );
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();
    PhysicsCube.long_geometries.set( key, geometry );
    return geometry;
  }

  /** Prepares separate colour/normal UV layouts for the authored 1x4/1x5 models. */
  private static prepareLongBlockUVs( root: THREE.Object3D, is_plastic: boolean ): void
  {
    root.updateMatrixWorld( true );
    const bounds = new THREE.Box3().setFromObject( root );
    const size = bounds.getSize( new THREE.Vector3() );
    if ( size.x <= 0 || size.y <= 0 )
      return;

    const world_position = new THREE.Vector3();
    const world_normal = new THREE.Vector3();

    root.traverse( child =>
    {
      if ( !( child instanceof THREE.Mesh ) )
        return;

      const geometry = ( child.geometry as THREE.BufferGeometry ).clone();
      const position = geometry.getAttribute( "position" );
      const normal = geometry.getAttribute( "normal" );
      const uv = geometry.getAttribute( "uv" );
      if ( !position || !normal || !uv )
        return;

      // Plastic's authored colour UVs keep its frame and emblem at a fixed
      // one-cell size. Its collapsed stretch bands are unsuitable for a
      // normal map, so uv1 starts as a copy and gets a tiny continuous spread.
      const normal_uv = is_plastic ? uv.clone() : null;

      const normal_matrix = new THREE.Matrix3().getNormalMatrix( child.matrixWorld );
      for ( let i = 0; i < position.count; i++ )
      {
        world_normal.fromBufferAttribute( normal, i ).applyMatrix3( normal_matrix ).normalize();
        if ( Math.abs( world_normal.z ) < 0.05 )
          continue;

        world_position.fromBufferAttribute( position, i ).applyMatrix4( child.matrixWorld );
        const x = THREE.MathUtils.clamp( ( world_position.x - bounds.min.x ) / size.x, 0, 1 );
        const y = THREE.MathUtils.clamp( ( world_position.y - bounds.min.y ) / size.y, 0, 1 );
        const is_vertical = size.y >= size.x;
        const along = is_vertical ? y : x;
        const across = is_vertical ? x : 1 - y;

        if ( normal_uv )
        {
          const authored_v = uv.getY( i );
          const is_stretch_band = Math.abs( authored_v - 0.299 ) < 0.012
            || Math.abs( authored_v - 0.701 ) < 0.012;
          if ( is_stretch_band )
            normal_uv.setY( i, authored_v + ( along - 0.5 ) * 0.04 );
          continue;
        }

        // Use the large right-hand atlas island (U = 1/3..2/3). Stretch its
        // plain upper/lower areas while keeping the emblem in a one-cell slice.
        const centre_fraction = Math.min( size.x, size.y ) / Math.max( size.x, size.y );
        const centre_min = 0.5 - centre_fraction * 0.5;
        const centre_max = 0.5 + centre_fraction * 0.5;
        const v = along < centre_min
          ? THREE.MathUtils.lerp( 0.04, 0.35, along / centre_min )
          : along <= centre_max
            ? THREE.MathUtils.lerp( 0.35, 0.75, ( along - centre_min ) / Math.max( centre_fraction, 0.0001 ) )
            : THREE.MathUtils.lerp( 0.75, 0.96, ( along - centre_max ) / Math.max( 1 - centre_max, 0.0001 ) );
        const u = world_normal.z >= 0
          ? THREE.MathUtils.lerp( 0.335, 0.665, across )
          : THREE.MathUtils.lerp( 0.665, 0.335, across );
        uv.setXY( i, u, v );
      }

      uv.needsUpdate = true;
      if ( normal_uv )
      {
        normal_uv.needsUpdate = true;
        geometry.setAttribute( "uv1", normal_uv );
      }
      child.geometry = geometry;
    } );
  }

  private static getAuthoredModel(
    width: number,
    height: number,
    material_type: CubeMaterialType
  ): ResourceID | undefined
  {
    const is_plastic = material_type === CubeMaterialType.PLASTIC;

    if ( width === 2 && height === 2 )
      return is_plastic ? ResourceID.PlasticBlock2x2Model : ResourceID.Block2x2Model;

    if ( width !== 1 && height !== 1 )
      return undefined;

    const blocks = Math.max( width, height );
    return is_plastic
      ? PhysicsCube.PLASTIC_MODELS[blocks]
      : PhysicsCube.AUTHORED_MODELS[blocks];
  }

  private static readonly smooth_materials = new Map<string, THREE.Material>();
  private static readonly long_plastic_atlases = new Map<string, THREE.Texture>();
  private static readonly authored_uv_debugged = new Set<string>();
  private static readonly plastic_2x2_debugged_stages = new Set<string>();

  private static logPlastic2x2Setup(
    stage: "source" | "final",
    root: THREE.Object3D,
    texture_id: ResourceID,
    normal_id: ResourceID
  ): void
  {
    if ( PhysicsCube.plastic_2x2_debugged_stages.has( stage ) )
      return;
    PhysicsCube.plastic_2x2_debugged_stages.add( stage );

    root.updateMatrixWorld( true );
    const bounds = new THREE.Box3().setFromObject( root );
    const meshes: Array<Record<string, unknown>> = [];

    root.traverse( child =>
    {
      if ( !( child instanceof THREE.Mesh ) )
        return;

      const geometry = child.geometry as THREE.BufferGeometry;
      geometry.computeBoundingBox();
      const position = geometry.getAttribute( "position" );
      const uv = geometry.getAttribute( "uv" );
      let uv_min: [number, number] | null = null;
      let uv_max: [number, number] | null = null;

      if ( uv )
      {
        let min_u = Infinity;
        let min_v = Infinity;
        let max_u = -Infinity;
        let max_v = -Infinity;
        for ( let i = 0; i < uv.count; i++ )
        {
          min_u = Math.min( min_u, uv.getX( i ) );
          min_v = Math.min( min_v, uv.getY( i ) );
          max_u = Math.max( max_u, uv.getX( i ) );
          max_v = Math.max( max_v, uv.getY( i ) );
        }
        uv_min = [Number( min_u.toFixed( 4 ) ), Number( min_v.toFixed( 4 ) )];
        uv_max = [Number( max_u.toFixed( 4 ) ), Number( max_v.toFixed( 4 ) )];
      }

      const material = ( Array.isArray( child.material ) ? child.material[0] : child.material ) as THREE.Material & {
        map?: THREE.Texture | null;
        normalMap?: THREE.Texture | null;
      };
      meshes.push( {
        name: child.name,
        geometry_uuid: geometry.uuid,
        vertices: position?.count ?? 0,
        indexed: geometry.index !== null,
        local_bounds: geometry.boundingBox ? {
          min: geometry.boundingBox.min.toArray(),
          max: geometry.boundingBox.max.toArray(),
          size: geometry.boundingBox.getSize( new THREE.Vector3() ).toArray(),
        } : null,
        uv_count: uv?.count ?? 0,
        uv_min,
        uv_max,
        material: material.type,
        material_map_uuid: material.map?.uuid ?? null,
        material_normal_uuid: material.normalMap?.uuid ?? null,
      } );
    } );

    const texture = ResourceManager.getTexture( texture_id );
    const normal = ResourceManager.getTexture( normal_id );
    const textureInfo = ( map: THREE.Texture ) => ( {
      id: ResourceID[map === texture ? texture_id : normal_id],
      size: [
        ( map.image as { width?: number } | undefined )?.width ?? null,
        ( map.image as { height?: number } | undefined )?.height ?? null,
      ],
      uuid: map.uuid,
      flip_y: map.flipY,
      color_space: map.colorSpace,
      channel: map.channel,
    } );

    console.group( `[PLASTIC-2X2-DEBUG] ${stage}` );
    console.log( {
      expected_model: "PlasticBlock2x2Model",
      expected_asset: "assets/models/blocks/plastic_block_2x2.fbx?v=2",
      requested_size: "2x2",
      world_bounds: {
        min: bounds.min.toArray(),
        max: bounds.max.toArray(),
        size: bounds.getSize( new THREE.Vector3() ).toArray(),
      },
      texture: textureInfo( texture ),
      normal: textureInfo( normal ),
    } );
    console.table( meshes );
    console.groupEnd();
  }

  private static logAuthoredBlockUVs(
    root: THREE.Object3D,
    width: number,
    height: number,
    model_id: ResourceID,
    texture_id: ResourceID | undefined,
    normal_id: ResourceID | undefined
  ): void
  {
    const key = `${model_id}|${texture_id ?? "none"}|${width}x${height}`;
    if ( PhysicsCube.authored_uv_debugged.has( key ) )
      return;
    PhysicsCube.authored_uv_debugged.add( key );

    root.updateMatrixWorld( true );
    const bounds = new THREE.Box3().setFromObject( root );
    const size = bounds.getSize( new THREE.Vector3() );
    const is_vertical = size.y >= size.x;
    const band_count = Math.max( 8, Math.ceil( Math.max( width, height ) * 2 ) );
    const bands = Array.from( { length: band_count }, ( _, index ) => ( {
      band: `${index + 1}/${band_count}`,
      vertices: 0,
      uv0_u_min: Infinity,
      uv0_u_max: -Infinity,
      uv0_v_min: Infinity,
      uv0_v_max: -Infinity,
      uv1_u_min: Infinity,
      uv1_u_max: -Infinity,
      uv1_v_min: Infinity,
      uv1_v_max: -Infinity,
    } ) );
    const materials: Array<Record<string, unknown>> = [];
    const coincident_front_vertices = new Map<string, Array<{
      position: [number, number, number];
      normal: [number, number, number];
      uv: [number, number];
    }>>();
    const world_position = new THREE.Vector3();
    const world_normal = new THREE.Vector3();

    root.traverse( child =>
    {
      if ( !( child instanceof THREE.Mesh ) )
        return;

      const geometry = child.geometry as THREE.BufferGeometry;
      const position = geometry.getAttribute( "position" );
      const normal = geometry.getAttribute( "normal" );
      const uv0 = geometry.getAttribute( "uv" );
      const uv1 = geometry.getAttribute( "uv1" );
      const normal_matrix = new THREE.Matrix3().getNormalMatrix( child.matrixWorld );

      if ( position && normal && uv0 )
      {
        for ( let i = 0; i < position.count; i++ )
        {
          world_normal.fromBufferAttribute( normal, i ).applyMatrix3( normal_matrix ).normalize();
          if ( Math.abs( world_normal.z ) < 0.05 )
            continue;

          world_position.fromBufferAttribute( position, i ).applyMatrix4( child.matrixWorld );
          const across = is_vertical
            ? ( world_position.x - bounds.min.x ) / Math.max( size.x, 0.0001 )
            : ( world_position.y - bounds.min.y ) / Math.max( size.y, 0.0001 );
          const along = is_vertical
            ? ( world_position.y - bounds.min.y ) / Math.max( size.y, 0.0001 )
            : ( world_position.x - bounds.min.x ) / Math.max( size.x, 0.0001 );
          const band_index = Math.min( band_count - 1, Math.max( 0, Math.floor( along * band_count ) ) );
          const band = bands[band_index];
          band.vertices++;
          band.uv0_u_min = Math.min( band.uv0_u_min, uv0.getX( i ) );
          band.uv0_u_max = Math.max( band.uv0_u_max, uv0.getX( i ) );
          band.uv0_v_min = Math.min( band.uv0_v_min, uv0.getY( i ) );
          band.uv0_v_max = Math.max( band.uv0_v_max, uv0.getY( i ) );
          if ( uv1 )
          {
            band.uv1_u_min = Math.min( band.uv1_u_min, uv1.getX( i ) );
            band.uv1_u_max = Math.max( band.uv1_u_max, uv1.getX( i ) );
            band.uv1_v_min = Math.min( band.uv1_v_min, uv1.getY( i ) );
            band.uv1_v_max = Math.max( band.uv1_v_max, uv1.getY( i ) );
          }

          // Ignore the outer bevel where UV seams are intentional. Coincident
          // vertices inside the front panel should agree exactly across every
          // generated stretch boundary.
          if ( world_normal.z > 0.5 && across > 0.08 && across < 0.92 )
          {
            const vertex_key = `${world_position.x.toFixed( 5 )}|${world_position.y.toFixed( 5 )}|${world_position.z.toFixed( 5 )}`;
            const samples = coincident_front_vertices.get( vertex_key ) ?? [];
            samples.push( {
              position: [world_position.x, world_position.y, world_position.z],
              normal: [world_normal.x, world_normal.y, world_normal.z],
              uv: [uv0.getX( i ), uv0.getY( i )],
            } );
            coincident_front_vertices.set( vertex_key, samples );
          }
        }
      }

      const mesh_materials = Array.isArray( child.material ) ? child.material : [child.material];
      mesh_materials.forEach( entry =>
      {
        const material = entry as THREE.Material & {
          map?: THREE.Texture | null;
          normalMap?: THREE.Texture | null;
          normalScale?: THREE.Vector2;
        };
        materials.push( {
          mesh: child.name,
          material: material.type,
          uv0: uv0?.count ?? 0,
          uv1: uv1?.count ?? 0,
          map_flip_y: material.map?.flipY ?? null,
          map_channel: material.map?.channel ?? null,
          normal_flip_y: material.normalMap?.flipY ?? null,
          normal_channel: material.normalMap?.channel ?? null,
          normal_scale: material.normalScale?.toArray() ?? null,
        } );
      } );
    } );

    const formatRange = ( min: number, max: number ): string | null => Number.isFinite( min )
      ? `${min.toFixed( 3 )}..${max.toFixed( 3 )}`
      : null;
    const rows = bands.map( band => ( {
      band: band.band,
      vertices: band.vertices,
      uv0_u: formatRange( band.uv0_u_min, band.uv0_u_max ),
      uv0_v: formatRange( band.uv0_v_min, band.uv0_v_max ),
      uv1_u: formatRange( band.uv1_u_min, band.uv1_u_max ),
      uv1_v: formatRange( band.uv1_v_min, band.uv1_v_max ),
    } ) );

    const map = texture_id === undefined ? null : ResourceManager.getTexture( texture_id );
    const normal_map = normal_id === undefined ? null : ResourceManager.getTexture( normal_id );
    const transition_audit = Array.from( coincident_front_vertices.values() )
      .filter( samples => samples.length > 1 )
      .map( samples =>
      {
        let max_uv_delta = 0;
        let min_normal_dot = 1;
        for ( let i = 0; i < samples.length; i++ )
          for ( let j = i + 1; j < samples.length; j++ )
          {
            max_uv_delta = Math.max(
              max_uv_delta,
              Math.hypot(
                samples[i].uv[0] - samples[j].uv[0],
                samples[i].uv[1] - samples[j].uv[1]
              )
            );
            min_normal_dot = Math.min(
              min_normal_dot,
              samples[i].normal[0] * samples[j].normal[0]
                + samples[i].normal[1] * samples[j].normal[1]
                + samples[i].normal[2] * samples[j].normal[2]
            );
          }
        return {
          y: Number( samples[0].position[1].toFixed( 5 ) ),
          copies: samples.length,
          max_uv_delta: Number( max_uv_delta.toFixed( 7 ) ),
          min_normal_dot: Number( min_normal_dot.toFixed( 7 ) ),
        };
      } )
      .filter( row => row.max_uv_delta > 0.00001 || row.min_normal_dot < 0.99999 )
      .sort( ( a, b ) => b.y - a.y )
      .slice( 0, 40 );
    console.groupCollapsed( `[BLOCK-UV-DEBUG] ${ResourceID[model_id]}|${texture_id === undefined ? "NO_TEXTURE" : ResourceID[texture_id]}|${width}x${height}` );
    console.log( {
      model: ResourceID[model_id],
      texture: texture_id === undefined ? null : ResourceID[texture_id],
      normal_map: normal_id === undefined ? null : ResourceID[normal_id],
      requested_size: `${width}x${height}`,
      orientation: is_vertical ? "vertical" : "horizontal",
      rotation_z: root.rotation.z,
      bounds: size.toArray().map( value => Number( value.toFixed( 3 ) ) ),
      atlas: map ? {
        size: [( map.image as { width?: number } | undefined )?.width, ( map.image as { height?: number } | undefined )?.height],
        flip_y: map.flipY,
        channel: map.channel,
      } : null,
      source_normal: normal_map ? {
        size: [( normal_map.image as { width?: number } | undefined )?.width, ( normal_map.image as { height?: number } | undefined )?.height],
        flip_y: normal_map.flipY,
        channel: normal_map.channel,
      } : null,
    } );
    console.table( materials );
    console.table( rows );
    console.log( "Interior coincident-vertex transition audit (empty is ideal):" );
    console.table( transition_audit );
    console.groupEnd();
  }

  private static liftStoneShadows( material: THREE.MeshLambertMaterial ): void
  {
    const mid = PhysicsCube.STONE_SHADOW_MID.toFixed( 4 );
    const gamma = PhysicsCube.STONE_SHADOW_GAMMA.toFixed( 4 );

    material.onBeforeCompile = shader =>
    {
      shader.fragmentShader = shader.fragmentShader
        .replace(
          "#include <common>",
          `#include <common>
          float stoneShadowLift( vec3 texel )
          {
            float luma = max( dot( texel, vec3( 0.2126, 0.7152, 0.0722 ) ), 1e-4 );
            return luma < ${mid} ? pow( luma / ${mid}, ${gamma} ) * ${mid} / luma : 1.0;
          }`
        )
        .replace(
          "#include <map_fragment>",
          `#include <map_fragment>
          diffuseColor.rgb *= stoneShadowLift( diffuseColor.rgb );`
        )
        .replace(
          "#include <emissivemap_fragment>",
          `#include <emissivemap_fragment>
          #ifdef USE_EMISSIVEMAP
            totalEmissiveRadiance *= stoneShadowLift( emissiveColor.rgb );
          #endif`
        );
    };
    material.customProgramCacheKey = () => "stone_shadow_lift";
  }

  private static createLongPlasticAtlas( source: THREE.Texture, blocks: number ): THREE.Texture
  {
    const key = `${source.uuid}|${blocks}`;
    const cached = PhysicsCube.long_plastic_atlases.get( key );
    if ( cached )
      return cached;

    const image = source.image as CanvasImageSource & { width: number; height: number };
    const canvas = document.createElement( "canvas" );
    canvas.width = image.width;
    canvas.height = image.height;
    const context = canvas.getContext( "2d" )!;
    context.drawImage( image, 0, 0, canvas.width, canvas.height );

    // Bake the old piecewise sampling into the atlas itself. Geometry can then
    // use one affine V coordinate, so GPU derivatives remain continuous while
    // the caps and centre emblem retain exactly their authored proportions.
    const atlas_x = canvas.width / 3;
    const atlas_width = canvas.width / 3;
    const extension = ( blocks - 3 ) * 0.5;
    const final_min = -extension;
    const segments = [
      { source_lo: 0, source_hi: 0.24, target_lo: final_min, target_hi: 0.24 - extension },
      { source_lo: 0.24, source_hi: 0.6, target_lo: 0.24 - extension, target_hi: 0.6 },
      { source_lo: 0.6, source_hi: 2.4, target_lo: 0.6, target_hi: 2.4 },
      { source_lo: 2.4, source_hi: 2.76, target_lo: 2.4, target_hi: 2.76 + extension },
      { source_lo: 2.76, source_hi: 3, target_lo: 2.76 + extension, target_hi: 3 + extension },
    ];

    for ( const segment of segments )
    {
      const source_y = ( 1 - segment.source_hi / 3 ) * canvas.height;
      const source_h = ( segment.source_hi - segment.source_lo ) / 3 * canvas.height;
      const target_y = ( 1 - ( segment.target_hi - final_min ) / blocks ) * canvas.height;
      const target_h = ( segment.target_hi - segment.target_lo ) / blocks * canvas.height;
      context.drawImage(
        image,
        atlas_x, source_y, atlas_width, source_h,
        atlas_x, target_y, atlas_width, target_h
      );
    }

    const baked = new THREE.CanvasTexture( canvas );
    baked.flipY = source.flipY;
    baked.colorSpace = source.colorSpace;
    baked.wrapS = source.wrapS;
    baked.wrapT = source.wrapT;
    baked.minFilter = source.minFilter;
    baked.magFilter = source.magFilter;
    baked.generateMipmaps = source.generateMipmaps;
    baked.anisotropy = source.anisotropy;
    baked.needsUpdate = true;
    PhysicsCube.long_plastic_atlases.set( key, baked );
    return baked;
  }

  private static createSmoothMaterial(
    source: THREE.Material | THREE.Material[],
    texture_id: ResourceID | undefined,
    flip_y = false,
    normal_id?: ResourceID,
    width = 1,
    height = 1
  ): THREE.Material
  {
    const source_key_material = Array.isArray( source ) ? source[0] : source;
    const source_key_color = "color" in source_key_material && source_key_material.color instanceof THREE.Color
      ? source_key_material.color.getHexString()
      : "ffffff";
    const is_plastic = texture_id !== undefined && PhysicsCube.PLASTIC_TEXTURES.has( texture_id );
    const is_long_plastic = is_plastic && Math.max( width, height ) >= 4;
    const plastic_blocks = Math.max( width, height );
    const plastic_orientation = is_plastic && Math.max( width, height ) > 1
      ? ( height > width ? "vertical" : "horizontal" )
      : "default";
    const cache_key = `${ texture_id ?? "none" }|${ flip_y ? 1 : 0 }|${ normal_id ?? "none" }|${ plastic_orientation }|${ is_long_plastic ? plastic_blocks : 0 }|${ texture_id === undefined ? source_key_color : "" }`;
    const cached = PhysicsCube.smooth_materials.get( cache_key );
    if ( cached )
      return cached;

    const is_authored_box = texture_id !== undefined && PhysicsCube.AUTHORED_BOX_TEXTURES.has( texture_id );
    const is_vivid_plastic = texture_id === ResourceID.PlasticPinkTexture
      || texture_id === ResourceID.PlasticPurpleTexture;
    let map = texture_id === undefined ? null : ResourceManager.getTexture( texture_id );
    if ( map )
    {
      map = map.clone();
      map.flipY = flip_y;
      map.minFilter = THREE.LinearMipmapLinearFilter;
      map.magFilter = THREE.LinearFilter;
      map.generateMipmaps = true;
      // 4x already covers the shallowest angle a block face reaches on screen;
      // higher taps multiply texture bandwidth on mobile for no visible gain.
      map.anisotropy = 4;
      map.needsUpdate = true;

      if ( is_long_plastic )
        map = PhysicsCube.createLongPlasticAtlas( map, plastic_blocks );
    }

    // Build a clean material, just like the table. Do not inherit the GLB's
    // roughness/AO/normal maps: their dense variation reads as sewn fabric.
    let normal_map = normal_id === undefined
      ? null
      : normal_id === ResourceID.AuthoredBoxNormal
        || normal_id === ResourceID.ColumnNormal
        || normal_id === ResourceID.RoundColumnNormal
        || normal_id === ResourceID.PlasticNewNormal
        || normal_id === ResourceID.Plastic2x2Normal
        || normal_id === ResourceID.Block2x2Normal
        || normal_id === ResourceID.StoneNormal
        ? ResourceManager.getTexture( normal_id )
        : ResourceManager.getNormalMap( normal_id );

    if ( normal_map && is_long_plastic )
      normal_map = PhysicsCube.createLongPlasticAtlas( normal_map, plastic_blocks );

    const source_material = Array.isArray( source ) ? source[0] : source;
    const source_color = "color" in source_material && source_material.color instanceof THREE.Color
      ? source_material.color
      : new THREE.Color( 0xffffff );

    if ( is_plastic )
    {
      const plastic = new THREE.MeshPhongMaterial( {
        map,
        normalMap: normal_map,
        normalScale: new THREE.Vector2( 1, 1 ),
        color: 0xffffff,

        // clearcoat: GlobalSettings.USE_CLEARCOAT ? 0.48 : 0,
        // clearcoatRoughness: 0.24,
        // emissiveMap modulates the emissive colour; without a non-black
        // emissive value, changing emissiveIntensity has no visible effect.
        // emissive: new THREE.Color( 0xffffff ),
        // emissiveMap: map,
        // emissiveIntensity: is_vivid_plastic ? 0.18 : 0.15,
        emissive: new THREE.Color( 0xffffff ),
        emissiveMap: map,
        emissiveIntensity: 0.25,
        shininess: 80,
        specular: new THREE.Color( 0x777777 ),
        side: THREE.FrontSide
      } );

      PhysicsCube.smooth_materials.set( cache_key, plastic );
      return plastic;
    }

    if ( texture_id === ResourceID.ColumnTexture || texture_id === ResourceID.RoundColumnTexture )
    {
      const column = new THREE.MeshPhongMaterial( {
        map,
        normalMap: normal_map,
        // glTF UVs with flipY off mirror the tangent frame's V.
        normalScale: new THREE.Vector2( 1, -1 ),
        color: 0xffffff,
        emissive: new THREE.Color( 0xffffff ),
        emissiveMap: map,
        emissiveIntensity: 0.12,
        // Polished marble and a gilded capital share one material: a
        // moderate highlight catches both without turning the shaft glossy.
        shininess: 36,
        specular: new THREE.Color( 0x3a3a3a ),
        side: THREE.FrontSide
      } );
      // Columns keep their relief and Phong highlight on every graphics tier.
      column.userData.keep_normal_map = true;
      column.userData.keep_specular = true;

      PhysicsCube.smooth_materials.set( cache_key, column );
      return column;
    }

    if ( texture_id === ResourceID.StoneTexture )
    {
      const stone = new THREE.MeshLambertMaterial( {
        map,
        normalMap: normal_map,
        // The cracks are narrow steep grooves: at full strength their walls
        // turn away from the light and read as black lines.
        normalScale: new THREE.Vector2( 0.55, 0.55 ),
        color: 0xffffff,
        emissive: new THREE.Color( 0xffffff ),
        emissiveMap: map,
        // Without a fill the blue reads greyer than the box and plastic
        // blocks standing next to it.
        emissiveIntensity: 0.22,
        side: THREE.FrontSide
      } );
      PhysicsCube.liftStoneShadows( stone );

      PhysicsCube.smooth_materials.set( cache_key, stone );
      return stone;
    }

    if ( is_authored_box )
    {
      const purple = new THREE.MeshLambertMaterial( {
        map,
        normalMap: normal_map,
        normalScale: new THREE.Vector2( 0.85, 0.85 ),
        // Keep the texture's original hue. A purple base colour multiplies
        // its RGB values and makes the already-purple artwork much darker.
        color: new THREE.Color( 1.08, 1.08, 1.08 ),

        emissive: new THREE.Color( 0xffffff ),
        emissiveMap: map,
        emissiveIntensity: 0.25,
        side: THREE.FrontSide
      } );

      PhysicsCube.smooth_materials.set( cache_key, purple );
      return purple;
    }

    const material = new THREE.MeshLambertMaterial( {
      map,
      normalMap: normal_map,
      normalScale: new THREE.Vector2( 0.45, 0.45 ),
      color: map === null ? source_color : 0xffffff,

      side: THREE.FrontSide
    } );

    PhysicsCube.smooth_materials.set( cache_key, material );
    return material;
  }

  private static clipSlab(
    geo: THREE.BufferGeometry,
    ai: number,
    lo: number,
    hi: number
  ): THREE.BufferGeometry
  {
    interface IClipVertex
    {
      px: number; py: number; pz: number;
      nx: number; ny: number; nz: number;
      u: number; v: number;
    }

    const pos = geo.attributes.position;
    const nrm = geo.attributes.normal;
    const uv = geo.attributes.uv;

    const readVertex = ( i: number ): IClipVertex => ( {
      px: pos.getX( i ), py: pos.getY( i ), pz: pos.getZ( i ),
      nx: nrm.getX( i ), ny: nrm.getY( i ), nz: nrm.getZ( i ),
      u: uv.getX( i ), v: uv.getY( i ),
    } );

    const axisOf = ( v: IClipVertex ): number =>
      ai === 0 ? v.px : ai === 1 ? v.py : v.pz;

    const lerpVertex = ( a: IClipVertex, b: IClipVertex, t: number ): IClipVertex => ( {
      px: a.px + ( b.px - a.px ) * t,
      py: a.py + ( b.py - a.py ) * t,
      pz: a.pz + ( b.pz - a.pz ) * t,
      nx: a.nx + ( b.nx - a.nx ) * t,
      ny: a.ny + ( b.ny - a.ny ) * t,
      nz: a.nz + ( b.nz - a.nz ) * t,
      u: a.u + ( b.u - a.u ) * t,
      v: a.v + ( b.v - a.v ) * t,
    } );

    const clipPoly = (
      poly: IClipVertex[],
      dist: ( v: IClipVertex ) => number
    ): IClipVertex[] =>
    {
      const out: IClipVertex[] = [];

      for ( let i = 0; i < poly.length; i++ )
      {
        const cur = poly[i];
        const next = poly[( i + 1 ) % poly.length];
        const d_cur = dist( cur );
        const d_next = dist( next );

        if ( d_cur >= 0 )
          out.push( cur );

        if ( ( d_cur >= 0 ) !== ( d_next >= 0 ) )
          out.push( lerpVertex( cur, next, d_cur / ( d_cur - d_next ) ) );
      }

      return out;
    };

    const result: IClipVertex[] = [];

    for ( let t = 0; t < pos.count; t += 3 )
    {
      let poly = [ readVertex( t ), readVertex( t + 1 ), readVertex( t + 2 ) ];

      poly = clipPoly( poly, v => axisOf( v ) - lo );
      if ( poly.length < 3 )
        continue;

      poly = clipPoly( poly, v => hi - axisOf( v ) );
      if ( poly.length < 3 )
        continue;

      for ( let i = 1; i < poly.length - 1; i++ )
        result.push( poly[0], poly[i], poly[i + 1] );
    }

    const out_pos = new Float32Array( result.length * 3 );
    const out_nrm = new Float32Array( result.length * 3 );
    const out_uv = new Float32Array( result.length * 2 );

    for ( let i = 0; i < result.length; i++ )
    {
      const v = result[i];
      out_pos[i * 3] = v.px; out_pos[i * 3 + 1] = v.py; out_pos[i * 3 + 2] = v.pz;
      out_nrm[i * 3] = v.nx; out_nrm[i * 3 + 1] = v.ny; out_nrm[i * 3 + 2] = v.nz;
      out_uv[i * 2] = v.u; out_uv[i * 2 + 1] = v.v;
    }

    const clipped = new THREE.BufferGeometry();
    clipped.setAttribute( "position", new THREE.BufferAttribute( out_pos, 3 ) );
    clipped.setAttribute( "normal", new THREE.BufferAttribute( out_nrm, 3 ) );
    clipped.setAttribute( "uv", new THREE.BufferAttribute( out_uv, 2 ) );

    return clipped;
  }

  private static remapAxis(
    geo: THREE.BufferGeometry,
    ai: number,
    from_lo: number,
    from_hi: number,
    to_lo: number,
    to_hi: number
  ): THREE.BufferGeometry
  {
    const pos = geo.attributes.position;
    const scale = ( to_hi - to_lo ) / ( from_hi - from_lo );
    const v = new THREE.Vector3();

    for ( let i = 0; i < pos.count; i++ )
    {
      v.fromBufferAttribute( pos, i );
      v.setComponent( ai, to_lo + ( v.getComponent( ai ) - from_lo ) * scale );
      pos.setXYZ( i, v.x, v.y, v.z );
    }

    return geo;
  }

  private static computeAxisCapParams(
    base: THREE.BufferGeometry,
    ai: number
  ): { a_min: number; a_max: number; half: number; cap_cut: number }
  {
    const bb = base.boundingBox!;
    const a_min = bb.min.getComponent( ai );
    const a_max = bb.max.getComponent( ai );
    const half = ( a_max - a_min ) * 0.5;
    const center = ( a_min + a_max ) * 0.5;

    const o1 = ( ai + 1 ) % 3;
    const o2 = ( ai + 2 ) % 3;
    const ext1 = Math.max( Math.abs( bb.min.getComponent( o1 ) ), Math.abs( bb.max.getComponent( o1 ) ) );
    const ext2 = Math.max( Math.abs( bb.min.getComponent( o2 ) ), Math.abs( bb.max.getComponent( o2 ) ) );

    const pos = base.attributes.position;
    const vtx = new THREE.Vector3();
    let smooth_c = 0;

    for ( let i = 0; i < pos.count; i++ )
    {
      vtx.fromBufferAttribute( pos, i );
      const lat = Math.max(
        Math.abs( vtx.getComponent( o1 ) ) / ext1,
        Math.abs( vtx.getComponent( o2 ) ) / ext2
      );

      if ( lat > 0.999 )
        smooth_c = Math.max( smooth_c, Math.abs( vtx.getComponent( ai ) - center ) );
    }

    if ( smooth_c < half * 0.2 )
      smooth_c = half * 0.8;

    return { a_min, a_max, half, cap_cut: smooth_c * 0.78 };
  }

  private static mergeAlongAxis(
    source: THREE.BufferGeometry,
    ai: number,
    blocks: number,
    params: { a_min: number; a_max: number; half: number; cap_cut: number; cap_cut_bottom?: number },
    gap_ratio = 0,
    stretch_band_ratio = 0.02,
    stretch_subdivisions = 1
  ): THREE.BufferGeometry
  {
    const { a_min, a_max, half, cap_cut } = params;
    const cap_cut_bottom = params.cap_cut_bottom ?? cap_cut;
    const center_axis = ( a_min + a_max ) * 0.5;
    const cut_top = center_axis + cap_cut;
    const cut_bottom = center_axis - cap_cut_bottom;
    const stretch_band = half * stretch_band_ratio;
    const extension = ( blocks - 1 ) * half * ( 1 + gap_ratio );

    const center = PhysicsCube.clipSlab( source, ai, cut_bottom, cut_top );

    // Split the source into adjacent, non-overlapping regions. The old bands
    // overlapped the centre/caps and restarted their UVs at each join, which
    // produced visible brightness steps even though the geometry was closed.
    const band_top_end = Math.min( a_max, cut_top + stretch_band );
    const band_bottom_start = Math.max( a_min, cut_bottom - stretch_band );

    // Keep the geometric extension as one band per side. Plastic textures use
    // a baked atlas plus affine UVs, so they no longer need extra subdivisions
    // to hide sampling-density changes.
    const makeSmoothBand = (
      from: number,
      to: number,
      offset_from: number,
      offset_to: number
    ): THREE.BufferGeometry =>
    {
      const subdivisions = stretch_subdivisions;
      const parts: THREE.BufferGeometry[] = [];
      const ease = ( t: number ): number => t * t * ( 3 - 2 * t );

      for ( let i = 0; i < subdivisions; i++ )
      {
        const t0 = i / subdivisions;
        const t1 = ( i + 1 ) / subdivisions;
        const source_lo = THREE.MathUtils.lerp( from, to, t0 );
        const source_hi = THREE.MathUtils.lerp( from, to, t1 );
        const offset_lo = THREE.MathUtils.lerp( offset_from, offset_to, ease( t0 ) );
        const offset_hi = THREE.MathUtils.lerp( offset_from, offset_to, ease( t1 ) );
        const part = PhysicsCube.clipSlab( source, ai, source_lo, source_hi );
        parts.push( PhysicsCube.remapAxis(
          part,
          ai,
          source_lo,
          source_hi,
          source_lo + offset_lo,
          source_hi + offset_hi
        ) );
      }

      return mergeGeometries( parts );
    };

    const band_top = makeSmoothBand( cut_top, band_top_end, 0, extension );

    const cap_top = PhysicsCube.clipSlab( source, ai, band_top_end, a_max + half );
    PhysicsCube.remapAxis(
      cap_top,
      ai,
      band_top_end,
      a_max,
      band_top_end + extension,
      a_max + extension
    );

    const band_bottom = makeSmoothBand( band_bottom_start, cut_bottom, -extension, 0 );

    const cap_bottom = PhysicsCube.clipSlab( source, ai, a_min - half, band_bottom_start );
    PhysicsCube.remapAxis(
      cap_bottom,
      ai,
      a_min,
      band_bottom_start,
      a_min - extension,
      band_bottom_start - extension
    );

    return mergeGeometries( [
      cap_bottom,
      band_bottom,
      center,
      band_top,
      cap_top
    ] );
  }

  private static prepareGridGeometry(
    model: ResourceID,
    source: THREE.BufferGeometry,
    width: number,
    height: number
  ): THREE.BufferGeometry
  {
    const key = `${model}_${width}x${height}`;
    const cached = this.long_geometries.get( key );
    if ( cached )
      return cached;

    const base = source.toNonIndexed();
    if ( !base.attributes.normal )
      base.computeVertexNormals();

    base.computeBoundingBox();

    let geo = base;

    if ( width > 1 )
    {
      const horizontal_params = PhysicsCube.computeAxisCapParams( base, 0 );
      horizontal_params.cap_cut = horizontal_params.half * PhysicsCube.HORIZONTAL_CAP_CUT_RATIO;
      geo = PhysicsCube.mergeAlongAxis( base, 0, width, horizontal_params, PhysicsCube.GRID_GAP_RATIO );
    }

    if ( height > 1 )
    {
      const vertical_params = PhysicsCube.computeAxisCapParams( base, 1 );
      vertical_params.cap_cut = vertical_params.half * PhysicsCube.VERTICAL_CAP_CUT_RATIO;
      geo = PhysicsCube.mergeAlongAxis( geo, 1, height, {
        ...vertical_params,
        cap_cut_bottom: vertical_params.half * PhysicsCube.VERTICAL_CAP_CUT_RATIO,
      } );
    }

    geo.computeBoundingBox();
    geo.computeBoundingSphere();

    this.long_geometries.set( key, geo );

    return geo;
  }

  /** Extends an authored vertical 1x3 FBX to 1x4/1x5 without scaling its details. */
  private static prepareAuthoredLongGeometry(
    model: ResourceID,
    source: THREE.BufferGeometry,
    blocks: number,
    bake_affine_uv: boolean
  ): THREE.BufferGeometry
  {
    const key = `authored_${model}_1x${blocks}_${bake_affine_uv ? "affine" : "sliced"}`;
    const cached = this.long_geometries.get( key );
    if ( cached )
      return cached;

    const base = source.toNonIndexed();
    if ( !base.attributes.normal )
      base.computeVertexNormals();
    base.computeBoundingBox();

    // FBXLoader converts the authored vertical direction to world Y through
    // the mesh transform; inside the BufferGeometry that direction is local Z.
    const length_axis = 2;
    const params = PhysicsCube.computeAxisCapParams( base, length_axis );
    params.cap_cut = params.half * PhysicsCube.VERTICAL_CAP_CUT_RATIO;

    // mergeAlongAxis accepts a scale expressed in source lengths. The source
    // is 3 cells long, so 4/3 and 5/3 add exactly one or two neutral sections.
    const geometry = PhysicsCube.mergeAlongAxis(
      base,
      length_axis,
      blocks / 3,
      params,
      0,
      // Sample a real neutral part of the authored face instead of the old
      // 2%-wide sliver. Stretching that near-single texel caused the visible
      // horizontal bands on smooth plastic gradients.
      0.24,
      bake_affine_uv ? 1 : 12
    );

    if ( bake_affine_uv )
    {
      geometry.computeBoundingBox();
      const bounds = geometry.boundingBox!;
      const uv = geometry.getAttribute( "uv" );
      const position = geometry.getAttribute( "position" );
      const length = bounds.max.z - bounds.min.z;

      for ( let i = 0; i < uv.count; i++ )
      {
        // U=1/3..2/3 is the authored long-panel island. Give it one affine
        // coordinate over the complete block; the matching texture is baked
        // below, so neither colour nor normal sampling changes density at a
        // geometric section boundary.
        if ( uv.getX( i ) >= 0.333 && uv.getX( i ) <= 0.667 )
          uv.setY( i, ( position.getZ( i ) - bounds.min.z ) / length );
      }
      uv.needsUpdate = true;
    }
    geometry.computeBoundingBox();
    geometry.computeBoundingSphere();

    this.long_geometries.set( key, geometry );
    return geometry;
  }

  public static getSize(): THREE.Vector3
  {
    if ( !this.size )
    {
      const mesh = ResourceManager.getModel( ResourceID.BoxModel );
      mesh.scale.setScalar( PhysicsCube.DEFAULT_SCALE );

      this.size = new THREE.Box3()
        .setFromObject( mesh )
        .getSize( new THREE.Vector3() );
    }

    return this.size.clone();
  }

  public hit(
    point: THREE.Vector3
  ): void
  {
    const direction = this.mesh.position
      .clone()
      .sub( point )
      .normalize();

    this.body.applyImpulse(
      {
        x: direction.x * 8,
        y: 4,
        z: direction.z * 8
      },
      true
    );
  }

  public checkDestroy( dt: number, table: PhysicsTable ): void
  {
    if ( this.destroying )
      return;

    const pos = this.body.translation();
    const is_outside = table.isOutside( pos );
    const bottom_y = pos.y - this.halfHeight();
    // In the table's own frame, not world Y: a block riding a swinging or
    // elevating table moves up and down with it without ever landing.
    const table_bottom_y = table.heightAboveSurface( pos ) - this.halfHeight();

    if ( !this.drop_sound_played && this.previous_bottom_y !== null && this.previous_table_bottom_y !== null )
    {
      const landed_on_table = !is_outside
        && this.previous_table_bottom_y > 0.2
        && table_bottom_y <= 0.2;
      const landed_on_ground = is_outside
        && this.previous_bottom_y > 0.2
        && bottom_y <= 0.2;

      if ( landed_on_table || landed_on_ground )
      {
        this.playDropSound( landed_on_ground ? 0.5 : 1 );
        this.drop_sound_played = true;
      }
    }
    this.previous_bottom_y = bottom_y;
    this.previous_table_bottom_y = table_bottom_y;

    if ( is_outside && bottom_y < 0.2 )
    {
      const linvel = this.body.linvel();
      const impact_speed = Math.sqrt( linvel.x * linvel.x + linvel.y * linvel.y + linvel.z * linvel.z );
      this.destroy( 0.0, impact_speed );
      return;
    }

    if ( !is_outside )
    {
      this.outside_pending = false;
    }
    else if ( !this.outside_pending && table.isBelowSurface( pos ) )
    {
      this.outside_pending = true;
      this.outside_timer = this.far_destroy_delay;
    }

    if ( this.outside_pending )
    {
      this.outside_timer -= dt;

      if ( this.outside_timer <= 0 )
      {
        const linvel = this.body.linvel();
        const impact_speed = Math.sqrt( linvel.x * linvel.x + linvel.y * linvel.y + linvel.z * linvel.z );
        this.destroy( 0.0, impact_speed );
      }
    }
  }

  public canBeHit(): boolean
  {
    return this.isAlive();
  }

  public onBallHit( point: THREE.Vector3 ): void
  {
    this.playHitSound();

    let impact_mat = BlockImpactMaterial.STONE;
    switch ( this.material_type )
    {
      case CubeMaterialType.STONE:
        impact_mat = BlockImpactMaterial.STONE;
        break;
      case CubeMaterialType.COLUMN:
      case CubeMaterialType.ROUND_COLUMN:
        impact_mat = BlockImpactMaterial.COLUMN;
        break;
      case CubeMaterialType.BRICK:
        impact_mat = BlockImpactMaterial.BRICK;
        break;
      case CubeMaterialType.PLYWOOD:
        if ( this.texture_id === ResourceID.GreenBoxTexture || this.texture_id === ResourceID.GreenBox2x2Texture )
          impact_mat = BlockImpactMaterial.GREEN_BOX;
        else if ( this.texture_id === ResourceID.PurpleBoxTexture || this.texture_id === ResourceID.PurpleBox2x2Texture )
          impact_mat = BlockImpactMaterial.PURPLE_BOX;
        else
          impact_mat = BlockImpactMaterial.WOOD;
        break;
      case CubeMaterialType.PLASTIC:
        switch ( this.texture_id )
        {
          case ResourceID.PlasticBlueTexture:
          case ResourceID.PlasticBlue2x2Texture:
            impact_mat = BlockImpactMaterial.PLASTIC_BLUE;
            break;
          case ResourceID.PlasticGreenTexture:
          case ResourceID.PlasticGreen2x2Texture:
            impact_mat = BlockImpactMaterial.PLASTIC_GREEN;
            break;
          case ResourceID.PlasticLightBlueTexture:
          case ResourceID.PlasticLightBlue2x2Texture:
            impact_mat = BlockImpactMaterial.PLASTIC_LIGHT_BLUE;
            break;
          case ResourceID.PlasticOrangeTexture:
          case ResourceID.PlasticOrange2x2Texture:
            impact_mat = BlockImpactMaterial.PLASTIC_ORANGE;
            break;
          case ResourceID.PlasticPinkTexture:
          case ResourceID.PlasticPink2x2Texture:
            impact_mat = BlockImpactMaterial.PLASTIC_PINK;
            break;
          case ResourceID.PlasticPurpleTexture:
          case ResourceID.PlasticPurple2x2Texture:
            impact_mat = BlockImpactMaterial.PLASTIC_PURPLE;
            break;
          case ResourceID.PlasticRedTexture:
          case ResourceID.PlasticRed2x2Texture:
            impact_mat = BlockImpactMaterial.PLASTIC_RED;
            break;
          case ResourceID.PlasticYellowTexture:
          case ResourceID.PlasticYellow2x2Texture:
            impact_mat = BlockImpactMaterial.PLASTIC_YELLOW;
            break;
          default:
            impact_mat = BlockImpactMaterial.PLASTIC;
            break;
        }
        break;
    }

    Vfx3DManager.addCannonBallBlockImpact( point, impact_mat );
    void hapticsController.vibrate( HapticPattern.MEDIUM, VibrationContext.GENERAL );
  }

  public onFireworkHit( point: THREE.Vector3 ): void
  {
    if ( !this.markDestroyed() ) return;

    // A rocket guarantees the shatter effect for every cube material,
    // including stone (normally thresholded) and plastic (normally fade-only).
    this.spawnShatterVfx(
      0,
      Math.max( this.getImpactSpeed(), PhysicsCube.STONE_SHATTER_IMPACT_SPEED )
    );
  }

  public checkSelfDestruct( dt: number, table: PhysicsTable ): void
  {
    this.checkDestroy( dt, table );   // те, що вже є
  }

  public playHitSound(): void
  {
    const clip = this.isStoneLike()
      ? AudioClips.METAL_CUBE_HIT
      : AudioClips.WOOD_CUBE_HIT;

    void audioController.playSound( clip );
  }

  public playDropSound( volume = 1 ): void
  {
    const use_first_variant = Math.random() < 0.5;
    const clip = this.isStoneLike()
      ? ( use_first_variant ? AudioClips.METAL_CUBE_DROP_1 : AudioClips.METAL_CUBE_DROP_2 )
      : ( use_first_variant ? AudioClips.WOOD_CUBE_DROP_1 : AudioClips.WOOD_CUBE_DROP_2 );

    void audioController.playSound( clip, false, volume );
  }

  public isAtRest(): boolean
  {
    if ( this.destroying ) return true;
    return !this.outside_pending && super.isAtRest();
  }

  /** The linger-then-fade corpse: gone for gameplay, still lying on the ground until the fade finishes. */
  public keepCorpse(): boolean
  {
    return this.destroying && !this.dead;
  }

  private fade_timer = 0;
  private fade_duration = PhysicsCube.FADE_DURATION;
  private linger_timer = 0;
  private static readonly FADE_DURATION = 0.35;

  /** A cube that goes out whole (no debris) lies where it landed for a beat, then melts into alpha like debris does. */
  private static readonly SETTLE_LINGER_DURATION = 1.2;
  private static readonly SETTLE_FADE_DURATION = 0.5;

  private setOpacity( opacity: number ): void
  {
    this.mesh.traverse( child =>
    {
      if ( child instanceof THREE.Mesh )
      {
        if ( !child.userData.materialCloned )
        {
          // clone() drops shader patches (stone's shadow lift), so carry them over.
          const cloneMaterial = ( m: THREE.Material ) =>
          {
            const copy = m.clone();
            copy.onBeforeCompile = m.onBeforeCompile;
            copy.customProgramCacheKey = m.customProgramCacheKey;
            return copy;
          };
          if ( Array.isArray( child.material ) )
          {
            child.material = child.material.map( cloneMaterial );
          }
          else
          {
            child.material = cloneMaterial( child.material );
          }
          child.userData.materialCloned = true;
        }

        const mats = Array.isArray( child.material ) ? child.material : [ child.material ];
        for ( const mat of mats )
        {
          mat.transparent = true;
          mat.opacity = opacity;
          mat.depthWrite = false;
        }
        child.castShadow = false;
      }
    } );
  }

  protected updateDestroying( dt: number ): void
  {
    if ( this.linger_timer > 0 )
    {
      this.linger_timer -= dt;
      return;
    }

    this.fade_timer -= dt;
    this.setOpacity( Math.max( 0, this.fade_timer / this.fade_duration ) );
    if ( this.fade_timer <= 0 ) this.markDead();
  }

  /** A stone only cracks when it comes down THIS hard; a plain topple off the table (~6-7) leaves it whole. */
  public static readonly STONE_SHATTER_IMPACT_SPEED = 8;

  public destroy( floor_y: number, impact_speed: number = 0.0 ): void
  {
    if ( !this.markDestroyed() ) return;

    if ( this.shouldShatter( impact_speed ) )
    {
      this.spawnShatterVfx( floor_y, impact_speed );
      return;
    }

    if ( SimulationContext.enabled )
      return;

    this.linger_timer = PhysicsCube.SETTLE_LINGER_DURATION;
    this.fade_duration = PhysicsCube.SETTLE_FADE_DURATION;
    this.fade_timer = PhysicsCube.SETTLE_FADE_DURATION;
  }

  /** Stone and both columns sound and break the same way. */
  private isStoneLike(): boolean
  {
    return this.material_type === CubeMaterialType.STONE
      || this.material_type === CubeMaterialType.COLUMN
      || this.material_type === CubeMaterialType.ROUND_COLUMN;
  }

  /** Plastic bounces and never breaks; stone and column need a truly hard landing; everything else shatters as before. */
  private shouldShatter( impact_speed: number ): boolean
  {
    switch ( this.material_type )
    {
      case CubeMaterialType.PLASTIC: return false;
      case CubeMaterialType.STONE:
      case CubeMaterialType.COLUMN:
      case CubeMaterialType.ROUND_COLUMN:
        return impact_speed >= PhysicsCube.STONE_SHATTER_IMPACT_SPEED;
      default: return true;
    }
  }

  private spawnShatterVfx( floor_y: number, impact_speed: number ): void
  {
    const grid_size = new THREE.Vector2( this.width, this.height );

    Vfx3DManager.addBlockShatter(
      this.mesh.position,
      this.mesh.quaternion,
      PhysicsCube.DEFAULT_SCALE,
      this.texture_id ?? ResourceID.GreenBoxTexture,
      this.mass,
      floor_y,
      impact_speed,
      grid_size
    );
  }

  public dispose(): void
  {
    super.dispose();
  }
}
